import React, { useState, useEffect } from 'react';
import { authAPI } from '../services/api';
import { useToast } from '../components/Toast';

function PendingUsers() {
  const { showToast } = useToast();
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchPendingUsers();
  }, []);

  const fetchPendingUsers = async () => {
    try {
      setLoading(true);
      const response = await authAPI.getPendingUsers();
      setPendingUsers(response.data);
    } catch (err) {
      showToast('Failed to load pending users', 'error');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (userId) => {
    if (!window.confirm('Are you sure you want to approve this user?')) {
      return;
    }

    try {
      setProcessingId(userId);
      await authAPI.approveUser(userId);
      setPendingUsers(pendingUsers.filter(user => user._id !== userId));
      showToast('User approved successfully!', 'success');
    } catch (err) {
      showToast('Failed to approve user', 'error');
      console.error(err);
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (userId) => {
    if (!window.confirm('Are you sure you want to reject this user? This will delete their account.')) {
      return;
    }

    try {
      setProcessingId(userId);
      await authAPI.rejectUser(userId);
      setPendingUsers(pendingUsers.filter(user => user._id !== userId));
      showToast('User rejected successfully!', 'success');
    } catch (err) {
      showToast('Failed to reject user', 'error');
      console.error(err);
    } finally {
      setProcessingId(null);
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Filter users based on search
  const filteredUsers = pendingUsers.filter(user => 
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="loading">Loading pending users...</div>
    );
  }

  return (
    <div className="container">
      <div className="card">
        <div style={{ marginBottom: '24px' }}>
          <h2 style={{ color: '#667eea', fontSize: '22px', marginBottom: '4px' }}>
            👥 Pending User Approvals
          </h2>
          <p style={{ color: '#718096', fontSize: '13px', margin: 0 }}>
            {pendingUsers.length} user{pendingUsers.length !== 1 ? 's' : ''} waiting for approval
          </p>
        </div>

        {/* Search */}
        <div style={{ marginBottom: '20px' }}>
          <input
            type="text"
            placeholder="🔍 Search by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '1.5px solid #e2e8f0',
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none',
              transition: 'all 0.2s'
            }}
            onFocus={(e) => e.target.style.borderColor = '#667eea'}
            onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
          />
        </div>

        {filteredUsers.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px 24px', color: '#718096', fontSize: '14px' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>✅</div>
            <p style={{ marginBottom: '8px', fontSize: '15px' }}>
              {searchTerm ? 'No users found matching your search' : 'No pending user approvals'}
            </p>
            <p style={{ fontSize: '13px', color: '#a0aec0' }}>
              {searchTerm ? 'Try adjusting your search terms' : 'All users have been approved'}
            </p>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '16px' }}>
            {filteredUsers.map((user) => (
              <div key={user._id} style={{
                backgroundColor: '#f7fafc',
                borderRadius: '10px',
                padding: '18px',
                border: '1px solid #e2e8f0',
                transition: 'all 0.2s'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  gap: '16px'
                }}>
                  {/* User Info */}
                  <div style={{ flex: 1 }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      marginBottom: '8px'
                    }}>
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontWeight: '600',
                        fontSize: '16px'
                      }}>
                        {user.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <h3 style={{
                          fontSize: '16px',
                          fontWeight: '600',
                          color: '#1a202c',
                          margin: 0,
                          marginBottom: '2px'
                        }}>
                          {user.name}
                        </h3>
                        <p style={{
                          fontSize: '13px',
                          color: '#718096',
                          margin: 0
                        }}>
                          {user.email}
                        </p>
                      </div>
                    </div>
                    
                    <div style={{
                      fontSize: '12px',
                      color: '#a0aec0',
                      marginTop: '8px',
                      paddingLeft: '52px'
                    }}>
                      Registered on {formatDate(user.createdAt)}
                    </div>
                  </div>

                  {/* Actions */}
                  <div style={{
                    display: 'flex',
                    gap: '8px',
                    alignItems: 'center'
                  }}>
                    <button
                      onClick={() => handleApprove(user._id)}
                      disabled={processingId === user._id}
                      style={{
                        padding: '10px 20px',
                        background: processingId === user._id ? '#cbd5e0' : 'linear-gradient(135deg, #48bb78, #38a169)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: processingId === user._id ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s',
                        boxShadow: processingId === user._id ? 'none' : '0 2px 4px rgba(72, 187, 120, 0.2)'
                      }}
                      onMouseOver={(e) => {
                        if (processingId !== user._id) {
                          e.target.style.transform = 'translateY(-2px)';
                          e.target.style.boxShadow = '0 4px 8px rgba(72, 187, 120, 0.3)';
                        }
                      }}
                      onMouseOut={(e) => {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 2px 4px rgba(72, 187, 120, 0.2)';
                      }}
                    >
                      {processingId === user._id ? '⏳ Processing...' : '✓ Approve'}
                    </button>
                    <button
                      onClick={() => handleReject(user._id)}
                      disabled={processingId === user._id}
                      style={{
                        padding: '10px 20px',
                        background: processingId === user._id ? '#cbd5e0' : 'linear-gradient(135deg, #f56565, #e53e3e)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: processingId === user._id ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s',
                        boxShadow: processingId === user._id ? 'none' : '0 2px 4px rgba(245, 101, 101, 0.2)'
                      }}
                      onMouseOver={(e) => {
                        if (processingId !== user._id) {
                          e.target.style.transform = 'translateY(-2px)';
                          e.target.style.boxShadow = '0 4px 8px rgba(245, 101, 101, 0.3)';
                        }
                      }}
                      onMouseOut={(e) => {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 2px 4px rgba(245, 101, 101, 0.2)';
                      }}
                    >
                      ✕ Reject
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default PendingUsers;
